import { useState } from "react";
import { Copy, ChevronRight, ExternalLink, Download, Package } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ApiPlayground from "@/components/ApiPlayground";

const ENDPOINTS = [
  { 
    method: "POST", 
    path: "/v1/random/bytes", 
    desc: "Generate cryptographically secure random bytes from quantum entropy source. Supports multiple output formats and entropy sources.",
    tag: "Random",
    parameters: [
      { name: "size", type: "integer", required: true, desc: "Number of random bytes to generate (1-1048576)" },
      { name: "format", type: "string", required: false, desc: "Output format: 'hex', 'base64', 'binary'. Default: 'hex'" },
      { name: "source", type: "string", required: false, desc: "Entropy source: 'raw_qrng', 'qrng_drbg'. Default: 'qrng_drbg'" },
    ],
    response: `{
  "random_bytes": "a3f2e1d4c5b6a7f8e9d0c1b2a3f4e5d6...",
  "metadata": {
    "source": "qrng_drbg",
    "algorithm": "AES-CTR-256",
    "qrng_device": "qrng-prod-01",
    "entropy_quality": 1.0,
    "request_id": "req_a1b2c3d4e5f6",
    "latency_ms": 2.1,
    "timestamp": "2026-02-13T14:30:00Z",
    "nist_compliant": true
  }
}`,
    codeExamples: {
      python: `import requests

API_KEY = "qe_live_your_api_key"
BASE_URL = "https://api.qcoresecure.com"

response = requests.post(
    f"{BASE_URL}/v1/random/bytes",
    headers={
        "Authorization": f"Bearer {API_KEY}",
        "Content-Type": "application/json"
    },
    json={"size": 256, "format": "hex", "source": "qrng_drbg"}
)

data = response.json()
print(f"Random bytes: {data['random_bytes']}")`,
      curl: `curl -X POST https://api.qcoresecure.com/v1/random/bytes \\
  -H "Authorization: Bearer qe_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"size": 256, "format": "hex", "source": "qrng_drbg"}'`,
      go: `payload := map[string]interface{}{
    "size": 256, "format": "hex", "source": "qrng_drbg",
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/random/bytes", bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Content-Type", "application/json")
resp, _ := client.Do(req)`
    }
  },
  { 
    method: "POST", 
    path: "/v1/random/range", 
    desc: "Generate random integers within a specified range. Ideal for lottery systems, gaming, and statistical sampling applications.",
    tag: "Random",
    parameters: [
      { name: "min", type: "integer", required: true, desc: "Minimum value (inclusive)" },
      { name: "max", type: "integer", required: true, desc: "Maximum value (inclusive)" },
      { name: "count", type: "integer", required: false, desc: "Number of random integers to generate (1-10000). Default: 1" },
      { name: "unique", type: "boolean", required: false, desc: "Ensure all numbers are unique. Default: false" },
    ],
    response: `{
  "numbers": [42, 17, 89, 3, 56],
  "metadata": {
    "min": 1,
    "max": 100,
    "count": 5,
    "unique": true,
    "source": "qrng_drbg",
    "request_id": "req_r4ng3x7890",
    "latency_ms": 1.8,
    "timestamp": "2026-02-13T14:32:00Z"
  }
}`,
    codeExamples: {
      python: `import requests

response = requests.post(
    "https://api.qcoresecure.com/v1/random/range",
    headers={"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"},
    json={"min": 1, "max": 100, "count": 5, "unique": True}
)

data = response.json()
print(f"Random numbers: {data['numbers']}")`,
      curl: `curl -X POST https://api.qcoresecure.com/v1/random/range \\
  -H "Authorization: Bearer qe_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"min": 1, "max": 100, "count": 5, "unique": true}'`,
      go: `payload := map[string]interface{}{
    "min": 1, "max": 100, "count": 5, "unique": true,
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/random/range", bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer "+apiKey)
resp, _ := client.Do(req)`
    }
  },
  { 
    method: "GET", 
    path: "/v1/random/stream", 
    desc: "Establish a Server-Sent Events (SSE) stream for continuous random byte delivery. Ideal for real-time applications requiring constant entropy.",
    tag: "Random",
    parameters: [
      { name: "chunk_size", type: "integer", required: false, desc: "Bytes per chunk (32-4096). Default: 256" },
      { name: "interval_ms", type: "integer", required: false, desc: "Milliseconds between chunks (100-10000). Default: 1000" },
      { name: "format", type: "string", required: false, desc: "Output format: 'hex', 'base64'. Default: 'hex'" },
    ],
    response: `event: random
data: {"chunk": "f8a2c1e9b7d4...", "seq": 1, "timestamp": "2026-02-13T14:35:00.123Z"}

event: random
data: {"chunk": "3e7b9c2d1f6a...", "seq": 2, "timestamp": "2026-02-13T14:35:01.124Z"}

event: heartbeat
data: {"status": "active", "chunks_sent": 2, "bytes_total": 512}`,
    codeExamples: {
      python: `import requests

with requests.get(
    "https://api.qcoresecure.com/v1/random/stream",
    headers={"Authorization": f"Bearer {API_KEY}"},
    params={"chunk_size": 128, "interval_ms": 500},
    stream=True
) as response:
    for line in response.iter_lines():
        if line:
            print(line.decode('utf-8'))`,
      curl: `curl -N "https://api.qcoresecure.com/v1/random/stream?chunk_size=128&interval_ms=500" \\
  -H "Authorization: Bearer qe_live_your_api_key" \\
  -H "Accept: text/event-stream"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/random/stream?chunk_size=128", nil)
req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Accept", "text/event-stream")
resp, _ := client.Do(req)
reader := bufio.NewReader(resp.Body)
for { line, _ := reader.ReadString('\\n'); fmt.Print(line) }`
    }
  },
  { 
    method: "POST", 
    path: "/v1/vrf/generate", 
    desc: "Generate a Verifiable Random Function (VRF) output with cryptographic proof. The proof can be publicly verified without revealing the secret key.",
    tag: "VRF",
    parameters: [
      { name: "input", type: "string", required: true, desc: "Input data to generate VRF output (hex-encoded or UTF-8)" },
      { name: "key_id", type: "string", required: false, desc: "VRF key identifier. Uses default org key if not specified" },
      { name: "encoding", type: "string", required: false, desc: "Input encoding: 'hex', 'utf8'. Default: 'utf8'" },
    ],
    response: `{
  "output": "b7c4e8f2a1d9c6b5e4f3a2d1c0b9a8f7e6d5c4b3a2f1e0d9c8b7a6f5e4d3c2b1",
  "proof": "03a8b2c1d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9...",
  "public_key": "02f3e2d1c0b9a8f7e6d5c4b3a2f1e0d9c8b7a6f5e4d3c2b1a0f9e8d7c6b5a4f3e2",
  "metadata": {
    "key_id": "vrf_key_prod_01",
    "algorithm": "ECVRF-P256-SHA256",
    "input_hash": "sha256:9f86d08...",
    "request_id": "req_vrf_gen_123",
    "timestamp": "2026-02-13T14:40:00Z"
  }
}`,
    codeExamples: {
      python: `import requests

response = requests.post(
    "https://api.qcoresecure.com/v1/vrf/generate",
    headers={"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"},
    json={"input": "lottery_draw_2026_02_13", "encoding": "utf8"}
)

data = response.json()
print(f"VRF Output: {data['output']}")
print(f"Proof: {data['proof'][:64]}...")`,
      curl: `curl -X POST https://api.qcoresecure.com/v1/vrf/generate \\
  -H "Authorization: Bearer qe_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"input": "lottery_draw_2026_02_13", "encoding": "utf8"}'`,
      go: `payload := map[string]interface{}{
    "input": "lottery_draw_2026_02_13", "encoding": "utf8",
}
body, _ := json.Marshal(payload)
req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/vrf/generate", bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer "+apiKey)
resp, _ := client.Do(req)`
    }
  },
  { 
    method: "POST", 
    path: "/v1/vrf/verify", 
    desc: "Verify a VRF proof against the provided input and public key. Returns verification status and reconstructed output.",
    tag: "VRF",
    parameters: [
      { name: "input", type: "string", required: true, desc: "Original input data (hex-encoded or UTF-8)" },
      { name: "output", type: "string", required: true, desc: "VRF output to verify (hex-encoded)" },
      { name: "proof", type: "string", required: true, desc: "VRF proof (hex-encoded)" },
      { name: "public_key", type: "string", required: true, desc: "Public key used for verification (hex-encoded)" },
    ],
    response: `{
  "valid": true,
  "computed_output": "b7c4e8f2a1d9c6b5e4f3a2d1c0b9a8f7e6d5c4b3a2f1e0d9c8b7a6f5e4d3c2b1",
  "output_match": true,
  "metadata": {
    "algorithm": "ECVRF-P256-SHA256",
    "verification_time_ms": 3.2,
    "request_id": "req_vrf_ver_456",
    "timestamp": "2026-02-13T14:42:00Z"
  }
}`,
    codeExamples: {
      python: `import requests

response = requests.post(
    "https://api.qcoresecure.com/v1/vrf/verify",
    headers={"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"},
    json={
        "input": "lottery_draw_2026_02_13",
        "output": "b7c4e8f2a1d9...",
        "proof": "03a8b2c1d4e5f6...",
        "public_key": "02f3e2d1c0b9..."
    }
)
print(f"Valid: {response.json()['valid']}")`,
      curl: `curl -X POST https://api.qcoresecure.com/v1/vrf/verify \\
  -H "Authorization: Bearer qe_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "input": "lottery_draw_2026_02_13",
    "output": "b7c4e8f2a1d9...",
    "proof": "03a8b2c1d4e5f6...",
    "public_key": "02f3e2d1c0b9..."
  }'`,
      go: `payload := map[string]interface{}{
    "input": "lottery_draw_2026_02_13",
    "output": "b7c4e8f2a1d9...",
    "proof": "03a8b2c1d4e5f6...",
    "public_key": "02f3e2d1c0b9...",
}
req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/vrf/verify", bytes.NewBuffer(body))`
    }
  },
  { 
    method: "GET", 
    path: "/v1/health/status", 
    desc: "Get comprehensive system health status including QRNG device state, service availability, and compliance status.",
    tag: "Health",
    parameters: [
      { name: "include_devices", type: "boolean", required: false, desc: "Include detailed device status. Default: false" },
      { name: "include_services", type: "boolean", required: false, desc: "Include service health details. Default: false" },
    ],
    response: `{
  "status": "healthy",
  "uptime_seconds": 2592000,
  "version": "2.4.1",
  "devices": {
    "total": 4,
    "healthy": 4,
    "degraded": 0,
    "offline": 0
  },
  "services": {
    "api": "healthy",
    "entropy_pool": "healthy",
    "drbg_engine": "healthy",
    "vrf_service": "healthy"
  },
  "compliance": {
    "nist_sp800_90a": true,
    "nist_sp800_90b": true,
    "fips_140_3": true,
    "last_audit": "2026-01-15T00:00:00Z"
  },
  "timestamp": "2026-02-13T14:45:00Z"
}`,
    codeExamples: {
      python: `import requests

response = requests.get(
    "https://api.qcoresecure.com/v1/health/status",
    headers={"Authorization": f"Bearer {API_KEY}"},
    params={"include_devices": True, "include_services": True}
)

health = response.json()
print(f"System Status: {health['status']}")
print(f"Healthy Devices: {health['devices']['healthy']}/{health['devices']['total']}")`,
      curl: `curl "https://api.qcoresecure.com/v1/health/status?include_devices=true&include_services=true" \\
  -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/health/status?include_devices=true", nil)
req.Header.Set("Authorization", "Bearer "+apiKey)
resp, _ := client.Do(req)
var health map[string]interface{}
json.NewDecoder(resp.Body).Decode(&health)
fmt.Printf("Status: %s\\n", health["status"])`
    }
  },
  { 
    method: "GET", 
    path: "/v1/health/entropy", 
    desc: "Get real-time entropy pool status and quality metrics. Monitor entropy levels and QRNG throughput.",
    tag: "Health",
    parameters: [
      { name: "detailed", type: "boolean", required: false, desc: "Include per-device entropy metrics. Default: false" },
    ],
    response: `{
  "pool_status": "optimal",
  "pool_level_percent": 94.5,
  "pool_size_bytes": 1048576,
  "available_bytes": 991029,
  "entropy_rate_bps": 4194304,
  "quality_score": 0.9998,
  "sources": [
    {"device": "qrng-prod-01", "contribution_percent": 25.2, "health": "healthy"},
    {"device": "qrng-prod-02", "contribution_percent": 24.8, "health": "healthy"},
    {"device": "qrng-prod-03", "contribution_percent": 25.1, "health": "healthy"},
    {"device": "qrng-prod-04", "contribution_percent": 24.9, "health": "healthy"}
  ],
  "sp800_90b_tests": {
    "last_run": "2026-02-13T14:44:00Z",
    "all_passed": true,
    "min_entropy_estimate": 0.9997
  },
  "timestamp": "2026-02-13T14:46:00Z"
}`,
    codeExamples: {
      python: `import requests

response = requests.get(
    "https://api.qcoresecure.com/v1/health/entropy",
    headers={"Authorization": f"Bearer {API_KEY}"},
    params={"detailed": True}
)

entropy = response.json()
print(f"Pool Level: {entropy['pool_level_percent']}%")
print(f"Quality Score: {entropy['quality_score']}")`,
      curl: `curl "https://api.qcoresecure.com/v1/health/entropy?detailed=true" \\
  -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/health/entropy?detailed=true", nil)
req.Header.Set("Authorization", "Bearer "+apiKey)
resp, _ := client.Do(req)
var entropy map[string]interface{}
json.NewDecoder(resp.Body).Decode(&entropy)
fmt.Printf("Pool Level: %.1f%%\\n", entropy["pool_level_percent"])`
    }
  },
  { 
    method: "GET", 
    path: "/v1/stats/usage", 
    desc: "Retrieve API usage statistics for your organization. Includes request counts, bandwidth consumption, and billing metrics.",
    tag: "Stats",
    parameters: [
      { name: "period", type: "string", required: false, desc: "Time period: 'hour', 'day', 'week', 'month'. Default: 'day'" },
      { name: "start_date", type: "string", required: false, desc: "Start date (ISO 8601). Default: start of current period" },
      { name: "end_date", type: "string", required: false, desc: "End date (ISO 8601). Default: now" },
      { name: "group_by", type: "string", required: false, desc: "Group results by: 'hour', 'day', 'endpoint'. Default: none" },
    ],
    response: `{
  "summary": {"total_requests": 1247893, "avg_latency_ms": 2.4},
  "quota": {"plan": "enterprise", "monthly_limit": 100000000, "used": 37264512}
}`,
    codeExamples: {
      python: `response = requests.get("https://api.qcoresecure.com/v1/stats/usage", headers={"Authorization": f"Bearer {API_KEY}"}, params={"period": "month"})`,
      curl: `curl "https://api.qcoresecure.com/v1/stats/usage?period=month" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/stats/usage?period=month", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },

  // ── VRF Key Management ────────────────────────────────────────────────────────
  {
    method: "GET",
    path: "/v1/vrf/public-key",
    tag: "VRF",
    desc: "Retrieve your tenant's current ECVRF public key for independent proof verification. Public keys are per-tenant and can be rotated.",
    parameters: [],
    response: `{
  "tenant_id": "acme-corp",
  "public_key": "02a3f2e1d4c5b6a7f8e9d0c1b2a3f4e5d6...",
  "algorithm": "ECVRF-P256-SHA256-TAI",
  "version": 1,
  "created_at": "2026-01-15T10:00:00Z",
  "rotated_at": null
}`,
    codeExamples: {
      python: `response = requests.get("https://api.qcoresecure.com/v1/vrf/public-key", headers={"Authorization": f"Bearer {API_KEY}"})
key_info = response.json()
print(f"Public key (v{key_info['version']}): {key_info['public_key'][:32]}...")`,
      curl: `curl "https://api.qcoresecure.com/v1/vrf/public-key" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/vrf/public-key", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },
  {
    method: "POST",
    path: "/v1/vrf/rotate-key",
    tag: "VRF",
    desc: "Rotate your tenant's ECVRF signing key. After rotation, new proofs use the new key. Previous proofs signed with the old key will not verify.",
    parameters: [],
    response: `{
  "tenant_id": "acme-corp",
  "public_key": "03b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9...",
  "algorithm": "ECVRF-P256-SHA256-TAI",
  "version": 2,
  "rotated_at": "2026-02-13T14:30:00Z",
  "message": "Key rotated successfully. Update any services verifying proofs."
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/vrf/rotate-key", headers={"Authorization": f"Bearer {API_KEY}"})
new_key = response.json()
print(f"New public key (v{new_key['version']}): {new_key['public_key'][:32]}...")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/vrf/rotate-key" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/vrf/rotate-key", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },

  // ── PQC — ML-KEM-768 ─────────────────────────────────────────────────────────
  {
    method: "POST",
    path: "/v1/pqc/kem/keygen",
    tag: "PQC",
    desc: "ML-KEM-768 (FIPS 203): Generate a quantum-safe key pair for key encapsulation. Replaces RSA/ECDH key exchange. Returns encapsulation key (public) and decapsulation key (private).",
    parameters: [],
    response: `{
  "algorithm": "ML-KEM-768",
  "fips_standard": "FIPS 203",
  "encapsulation_key": "AAAA...",
  "decapsulation_key": "BBBB...",
  "ek_size_bytes": 1184,
  "dk_size_bytes": 2400
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/pqc/kem/keygen", headers={"Authorization": f"Bearer {API_KEY}"})
keys = response.json()
print(f"EK size: {keys['ek_size_bytes']}B, DK size: {keys['dk_size_bytes']}B")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/pqc/kem/keygen" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/pqc/kem/keygen", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },
  {
    method: "POST",
    path: "/v1/pqc/dsa/keygen",
    tag: "PQC",
    desc: "ML-DSA-65 (FIPS 204): Generate a quantum-safe key pair for digital signatures. Replaces ECDSA/RSA signatures.",
    parameters: [],
    response: `{
  "algorithm": "ML-DSA-65",
  "fips_standard": "FIPS 204",
  "public_key": "AAAA...",
  "private_key": "BBBB...",
  "pk_size_bytes": 1952,
  "sk_size_bytes": 4032
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/pqc/dsa/keygen", headers={"Authorization": f"Bearer {API_KEY}"})
keys = response.json()
print(f"PK size: {keys['pk_size_bytes']}B, SK size: {keys['sk_size_bytes']}B")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/pqc/dsa/keygen" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/pqc/dsa/keygen", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },
  {
    method: "POST",
    path: "/v1/pqc/dsa/sign",
    tag: "PQC",
    desc: "ML-DSA-65: Sign a message with a private key. Returns a quantum-safe digital signature (~3.3 KB).",
    parameters: [
      { name: "private_key", type: "string", required: true, desc: "Base64-encoded ML-DSA-65 private key" },
      { name: "message", type: "string", required: true, desc: "Message to sign (UTF-8)" },
    ],
    response: `{
  "algorithm": "ML-DSA-65",
  "signature": "CCCC...",
  "message_hash": "a3f2e1...",
  "sig_size_bytes": 3309
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/pqc/dsa/sign", headers={"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}, json={"private_key": sk_b64, "message": "Hello, quantum world!"})
print(response.json()["signature"][:32] + "...")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/pqc/dsa/sign" -H "Authorization: Bearer qe_live_your_api_key" -H "Content-Type: application/json" -d '{"private_key":"<base64>","message":"Hello!"}'`,
      go: `payload, _ := json.Marshal(map[string]string{"private_key": skB64, "message": "Hello!"}); req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/pqc/dsa/sign", bytes.NewBuffer(payload))`
    }
  },
  {
    method: "POST",
    path: "/v1/pqc/slhdsa/keygen",
    tag: "PQC",
    desc: "SLH-DSA-SHAKE-256s (FIPS 205 / SPHINCS+): Generate a hash-based quantum-safe key pair. NIST Level 5. Compact keys (64B public, 128B private). Signatures are ~29 KB.",
    parameters: [],
    response: `{
  "algorithm": "SLH-DSA-SHAKE-256s",
  "fips_standard": "FIPS 205 (SPHINCS+)",
  "security_level": "NIST Level 5 (~AES-256)",
  "public_key": "AAAA...",
  "private_key": "BBBB...",
  "pk_size_bytes": 64,
  "sk_size_bytes": 128
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/pqc/slhdsa/keygen", headers={"Authorization": f"Bearer {API_KEY}"})
keys = response.json()
print(f"PK: {keys['pk_size_bytes']}B, SK: {keys['sk_size_bytes']}B, Security: {keys['security_level']}")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/pqc/slhdsa/keygen" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/pqc/slhdsa/keygen", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },
  {
    method: "POST",
    path: "/v1/pqc/slhdsa/sign",
    tag: "PQC",
    desc: "SLH-DSA-SHAKE-256s: Sign a message with highest quantum security (NIST Level 5). Produces a ~29 KB hash-based signature with no lattice or number-theoretic assumptions.",
    parameters: [
      { name: "private_key", type: "string", required: true, desc: "Base64-encoded SLH-DSA-SHAKE-256s private key" },
      { name: "message", type: "string", required: true, desc: "Message to sign (UTF-8)" },
    ],
    response: `{
  "algorithm": "SLH-DSA-SHAKE-256s",
  "signature": "DDDD...",
  "message_hash": "b5c3f1...",
  "sig_size_bytes": 29792
}`,
    codeExamples: {
      python: `response = requests.post("https://api.qcoresecure.com/v1/pqc/slhdsa/sign", headers={"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}, json={"private_key": sk_b64, "message": "Critical data"})
sig = response.json()
print(f"Sig size: {sig['sig_size_bytes']}B")`,
      curl: `curl -X POST "https://api.qcoresecure.com/v1/pqc/slhdsa/sign" -H "Authorization: Bearer qe_live_your_api_key" -H "Content-Type: application/json" -d '{"private_key":"<base64>","message":"Critical data"}'`,
      go: `payload, _ := json.Marshal(map[string]string{"private_key": skB64, "message": "Critical data"}); req, _ := http.NewRequest("POST", "https://api.qcoresecure.com/v1/pqc/slhdsa/sign", bytes.NewBuffer(payload))`
    }
  },
  {
    method: "GET",
    path: "/v1/pqc/info",
    tag: "PQC",
    desc: "Get information about all supported Post-Quantum Cryptography algorithms: ML-KEM-768, ML-DSA-65, and SLH-DSA-SHAKE-256s with key sizes, standards, and use cases.",
    parameters: [],
    response: `{
  "status": "active",
  "algorithms": [
    {"name": "ML-KEM-768", "standard": "FIPS 203", "security_level": "NIST Level 3"},
    {"name": "ML-DSA-65",  "standard": "FIPS 204", "security_level": "NIST Level 3"},
    {"name": "SLH-DSA-SHAKE-256s", "standard": "FIPS 205", "security_level": "NIST Level 5"}
  ]
}`,
    codeExamples: {
      python: `response = requests.get("https://api.qcoresecure.com/v1/pqc/info", headers={"Authorization": f"Bearer {API_KEY}"})
for alg in response.json()["algorithms"]:
    print(f"{alg['name']}: {alg['standard']} ({alg['security_level']})")`,
      curl: `curl "https://api.qcoresecure.com/v1/pqc/info" -H "Authorization: Bearer qe_live_your_api_key"`,
      go: `req, _ := http.NewRequest("GET", "https://api.qcoresecure.com/v1/pqc/info", nil); req.Header.Set("Authorization", "Bearer "+apiKey)`
    }
  },
];

const SDK_LANGUAGES = [
  {
    id: "python",
    label: "Python",
    badge: "pip",
    version: "1.0.0",
    requires: "Python 3.9+",
    install: "pip install qcoresecure",
    color: "bg-blue-50 border-blue-200",
    badgeColor: "bg-blue-100 text-blue-700",
    cli_install: "pip install qcoresecure   # installs qraas CLI automatically",
    cli_usage: `# Save your API key (once)
qraas configure --api-key qe_live_...

# Generate random bytes (hex output)
qraas bytes --count 64 --output hex

# Random integer [1, 1000]
qraas integer --min 1 --max 1000

# Quantum UUID
qraas uuid

# VRF: generate + verify
qraas vrf generate "lottery-round-42"
qraas vrf verify --vrf-output <OUTPUT> --proof <PROOF> --input "lottery-round-42" --nonce <NONCE>

# Pipe raw bytes to another tool
qraas bytes --count 32 --output raw | base64`,
    quickstart: `from qcoresecure import QRaaS

# API Key mode (default)
client = QRaaS(api_key="qe_live_...", base_url="https://your-instance.com")

# Random bytes
result = client.random.bytes(32)
print(result["bytes_hex"])

# Verifiable Random Function
proof = client.vrf.generate("lottery-round-42")
ok = client.vrf.verify(proof["output"], proof["proof"], "lottery-round-42", proof["nonce"])

# JWT mode (account management)
client = QRaaS(email="you@company.com", password="...",
               totp_callback=lambda: input("TOTP: "))
keys = client.api_keys.list()`,
  },
  {
    id: "nodejs",
    label: "Node.js",
    badge: "npm",
    version: "1.0.0",
    requires: "Node.js 18+",
    install: "npm install qcoresecure",
    color: "bg-emerald-50 border-emerald-200",
    badgeColor: "bg-emerald-100 text-emerald-700",
    cli_install: "npm install -g qcoresecure   # installs qraas CLI globally",
    cli_usage: `# Save your API key (once)
qraas configure --api-key qe_live_...

# Generate random bytes (hex output)
qraas bytes --count 64 --output hex

# Random integer [1, 1000]
qraas integer --min 1 --max 1000

# Quantum UUID
qraas uuid

# VRF: generate proof
qraas vrf generate "lottery-round-42"

# List and create API keys
qraas keys list
qraas keys create "my-service-key"`,
    quickstart: `const { QRaaS } = require("qcoresecure");

// API Key mode
const client = new QRaaS({ apiKey: "qe_live_...", baseUrl: "https://your-instance.com" });

const bytes = await client.random.bytes(32);
console.log(bytes.bytes_hex);

const proof  = await client.vrf.generate("lottery-round-42");
const result = await client.vrf.verify(proof.output, proof.proof, "lottery-round-42", proof.nonce);
console.log(result.valid); // true

// JWT mode with 2FA
const jwt = new QRaaS({ email: "you@company.com", password: "...",
  totpCallback: async () => await promptUser("TOTP: ") });
await jwt.ready;
const keys = await jwt.apiKeys.list();`,
  },
  {
    id: "go",
    label: "Go",
    badge: "go get",
    version: "1.0.0",
    requires: "Go 1.21+",
    install: "go get github.com/qcoresecure/qcoresecure-go",
    color: "bg-cyan-50 border-cyan-200",
    badgeColor: "bg-cyan-100 text-cyan-700",
    cli_install: `go install github.com/qcoresecure/qcoresecure-go/cmd/qraas@latest`,
    cli_usage: `# Save your API key (once)
qraas configure --api-key qe_live_...

# Generate random bytes
qraas bytes --count 64 --output hex

# Random integer
qraas integer --min 1 --max 1000

# UUID
qraas uuid

# VRF
qraas vrf generate "lottery-round-42"

# API key management
qraas keys list
qraas keys create "my-service-key"`,
    quickstart: `import "github.com/qcoresecure/qcoresecure-go/qcoresecure"

client, _ := qcoresecure.New(qcoresecure.Config{
    APIKey:  "qe_live_...",
    BaseURL: "https://your-instance.com",
})

rb, _ := client.Random.Bytes(ctx, 32)
fmt.Println(rb["bytes_hex"])

proof, _ := client.VRF.Generate(ctx, "lottery-round-42", "")
check, _ := client.VRF.Verify(ctx,
    proof["output"].(string), proof["proof"].(string),
    "lottery-round-42", proof["nonce"].(string))
fmt.Println(check["valid"]) // true`,
  },
  {
    id: "rust",
    label: "Rust",
    badge: "cargo",
    version: "1.0.0",
    requires: "Rust 1.75+",
    install: `qcoresecure = "1.0.0"  # Cargo.toml`,
    color: "bg-orange-50 border-orange-200",
    badgeColor: "bg-orange-100 text-orange-700",
    cli_install: "cargo install qcoresecure   # installs qraas CLI",
    cli_usage: `# Save your API key (once)
qraas configure --api-key qe_live_...

# Generate random bytes (hex output)
qraas bytes --count 64 --output hex

# Random integer [1, 1000]
qraas integer --min 1 --max 1000

# UUID
qraas uuid

# VRF: generate proof
qraas vrf generate "lottery-round-42"

# Health check
qraas health

# Usage stats
qraas stats`,
    quickstart: `use qcoresecure::{Config, QRaaS};

let client = QRaaS::new(
    Config::with_api_key("qe_live_...")
        .base_url("https://your-instance.com"),
).await?;

let bytes = client.random().bytes(32).await?;
println!("{}", bytes["bytes_hex"]);

let proof = client.vrf().generate("lottery-round-42", None).await?;
let ok = client.vrf().verify(
    proof["output"].as_str().unwrap(), proof["proof"].as_str().unwrap(),
    "lottery-round-42", proof["nonce"].as_str().unwrap(),
).await?;
println!("{}", ok["valid"]); // true`,
  },
];

function SDKDownloads({ onCopy, copied }) {
  const [activeLang, setActiveLang] = useState("python");
  const sdk = SDK_LANGUAGES.find((s) => s.id === activeLang);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="space-y-4" data-testid="sdk-downloads-section">
      {/* Language selector */}
      <div className="flex gap-2 flex-wrap" data-testid="sdk-language-selector">
        {SDK_LANGUAGES.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveLang(s.id)}
            data-testid={`sdk-tab-${s.id}`}
            className={`px-4 py-2 text-sm font-medium border transition-colors ${
              activeLang === s.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sdk && (
        <div className={`border rounded-sm p-6 ${sdk.color}`} data-testid={`sdk-card-${sdk.id}`}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-slate-600" />
                <span className="font-semibold text-slate-900 text-sm">qcoresecure</span>
                <Badge className={`text-[10px] ${sdk.badgeColor} border-0`}>v{sdk.version}</Badge>
                <Badge variant="outline" className="text-[10px] text-slate-400">{sdk.requires}</Badge>
              </div>
              <p className="text-xs text-slate-500">Official QCoreSecure SDK for {sdk.label}</p>
            </div>
            <a
              href={`${backendUrl}/api/v1/sdks/download/${sdk.id}`}
              download
              data-testid={`sdk-download-btn-${sdk.id}`}
            >
              <Button size="sm" className="bg-slate-900 hover:bg-slate-700 text-white gap-1.5 text-xs">
                <Download className="w-3.5 h-3.5" />
                Download .zip
              </Button>
            </a>
          </div>

          {/* Install command */}
          <div className="mb-4">
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Install</div>
            <div className="relative bg-slate-950 px-4 py-3 flex items-center justify-between">
              <code className="text-xs font-mono text-emerald-400">{sdk.install}</code>
              <button
                onClick={() => onCopy(sdk.install)}
                className="text-slate-500 hover:text-slate-300 transition-colors ml-3"
                data-testid={`sdk-copy-install-${sdk.id}`}
              >
                {copied ? <span className="text-emerald-400 text-xs">Copied</span> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Quick-start code */}
          <div>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Quick Start</div>
            <div className="relative bg-slate-950 p-4 overflow-auto max-h-72">
              <button
                onClick={() => onCopy(sdk.quickstart)}
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 transition-colors"
                data-testid={`sdk-copy-quickstart-${sdk.id}`}
              >
                {copied ? <span className="text-emerald-400 text-xs">Copied</span> : <Copy className="w-3.5 h-3.5" />}
              </button>
              <pre className="text-xs font-mono text-slate-300 leading-relaxed whitespace-pre">{sdk.quickstart}</pre>
            </div>
          </div>

          {/* Auth modes note */}
          <div className="mt-4 pt-4 border-t border-slate-200/60 grid grid-cols-2 gap-3">
            <div className="text-xs">
              <span className="font-medium text-slate-700">API Key mode</span>
              <p className="text-slate-500 mt-0.5">Default. Pass your API key at init. Best for machine-to-machine.</p>
            </div>
            <div className="text-xs">
              <span className="font-medium text-slate-700">JWT mode</span>
              <p className="text-slate-500 mt-0.5">Email + password. Handles 2FA, token refresh. Best for account management.</p>
            </div>
          </div>

          {/* CLI section */}
          {sdk.cli_install && (
            <div className="mt-4 pt-4 border-t border-slate-200/60">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">CLI</span>
                <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 font-mono">qraas</span>
                <span className="text-[10px] text-slate-400">Command-line interface included</span>
              </div>
              <div className="relative bg-slate-950 px-4 py-2.5 flex items-center justify-between mb-3">
                <code className="text-xs font-mono text-emerald-400">{sdk.cli_install}</code>
                <button onClick={() => onCopy(sdk.cli_install)} className="text-slate-500 hover:text-slate-300 ml-3 transition-colors">
                  {copied ? <span className="text-emerald-400 text-xs">Copied</span> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <div className="relative bg-slate-950 p-4 overflow-auto max-h-72">
                <button onClick={() => onCopy(sdk.cli_usage)} className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 transition-colors">
                  {copied ? <span className="text-emerald-400 text-xs">Copied</span> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <pre className="text-xs font-mono text-slate-300 leading-relaxed whitespace-pre">{sdk.cli_usage}</pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer notice */}
      <p className="text-xs text-slate-400 pt-1">
        SDKs are structured for registry publishing (PyPI / npm / crates.io / pkg.go.dev). Registry release coming soon.
      </p>
    </div>
  );
}

export default function ApiDocs() {
  const [selectedEndpoint, setSelectedEndpoint] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentEndpoint = ENDPOINTS[selectedEndpoint];

  return (
    <div data-testid="api-docs-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="api-docs-title">API Documentation</h1>
          <p className="text-sm text-slate-500 mt-1">Complete reference for the Quantum Entropy API</p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/openapi.json`}
          download="qcoresecure-openapi.json"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded font-medium shrink-0"
          data-testid="download-openapi-btn"
          title="Download OpenAPI specification (JSON)"
        >
          <Download className="w-3.5 h-3.5" />
          OpenAPI Spec
        </a>
      </div>

      <Tabs defaultValue="reference" className="space-y-4">
        <TabsList className="bg-slate-100 h-9">
          <TabsTrigger value="reference" className="text-xs h-8 px-4" data-testid="tab-api-reference">API Reference</TabsTrigger>
          <TabsTrigger value="sdks" className="text-xs h-8 px-4" data-testid="tab-sdk-downloads">SDK Downloads</TabsTrigger>
        </TabsList>

        {/* ── API Reference ─────────────────────────────────────────────── */}
        <TabsContent value="reference" className="mt-0">
      <div className="grid lg:grid-cols-4 gap-4">
        {/* Endpoint Sidebar */}
        <div className="bg-white border border-slate-200 p-4" data-testid="api-endpoint-list">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">Endpoints</div>
          <div className="space-y-0.5">
            {(() => {
              const tagOrder = ["Random", "VRF", "PQC", "Health", "Stats"];
              const grouped = {};
              ENDPOINTS.forEach((ep, i) => {
                const tag = ep.tag || "Other";
                if (!grouped[tag]) grouped[tag] = [];
                grouped[tag].push({ ep, i });
              });
              return tagOrder.filter(t => grouped[t]).flatMap(tag => [
                <div key={`hdr-${tag}`} className="pt-2 pb-0.5">
                  <span className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold px-1">{tag}</span>
                </div>,
                ...grouped[tag].map(({ ep, i }) => (
                  <button
                    key={i}
                    onClick={() => setSelectedEndpoint(i)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${selectedEndpoint === i ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`endpoint-item-${i}`}
                  >
                    <Badge variant="outline" className={`text-[9px] font-mono px-1.5 shrink-0 ${selectedEndpoint === i ? 'border-slate-600 text-slate-300' : 'border-slate-200'}`}>
                      {ep.method}
                    </Badge>
                    <span className="text-xs font-mono truncate flex-1">{ep.path}</span>
                  </button>
                ))
              ]);
            })()}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">SDKs</div>
            {["python", "nodejs", "go", "rust"].map((id) => (
              <a
                key={id}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/sdks/download/${id}`}
                download
                className="flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                data-testid={`sdk-sidebar-${id}`}
              >
                {id === "nodejs" ? "Node.js" : id.charAt(0).toUpperCase() + id.slice(1)} SDK
                <Download className="w-3 h-3 text-slate-400" />
              </a>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-2">Spec</div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/openapi.json`}
              download="qcoresecure-openapi.json"
              className="flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="openapi-spec-download-sidebar"
            >
              OpenAPI 3.1 JSON
              <Download className="w-3 h-3 text-slate-400" />
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="swagger-ui-link"
            >
              Swagger UI
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/redoc`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="redoc-link"
            >
              ReDoc
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {/* Endpoint Detail */}
          <div className="bg-white border border-slate-200 p-6" data-testid="endpoint-detail">
            <div className="flex items-center gap-3 mb-2">
              <Badge className={`text-xs font-mono uppercase ${currentEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {currentEndpoint.method}
              </Badge>
              <code className="text-sm font-mono text-slate-900">{currentEndpoint.path}</code>
              <Badge variant="outline" className="text-[10px] text-slate-400">{currentEndpoint.tag}</Badge>
            </div>
            <p className="text-sm text-slate-600 mb-6">{currentEndpoint.desc}</p>

            {/* Parameters */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">Request Parameters</h4>
              <div className="border border-slate-100">
                {currentEndpoint.parameters.map((param, i) => (
                  <div key={i} className={`flex items-start gap-4 p-3 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                    <code className="text-xs font-mono text-cyan-700 bg-cyan-50 px-1.5 py-0.5 whitespace-nowrap">{param.name}</code>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-slate-400 font-mono">{param.type}</span>
                        {param.required && <Badge className="bg-red-50 text-red-600 text-[9px] border-0">required</Badge>}
                      </div>
                      <div className="text-xs text-slate-600">{param.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Response */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">Response</h4>
              <div className="relative bg-slate-950 p-4 overflow-auto max-h-96">
                <button onClick={() => handleCopy(currentEndpoint.response)} className="absolute top-3 right-3 p-1.5 text-slate-500 hover:text-slate-300 transition-colors" data-testid="copy-response">
                  {copied ? <span className="text-xs text-emerald-400">Copied</span> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <pre className="text-xs font-mono text-slate-300 leading-relaxed whitespace-pre">{currentEndpoint.response}</pre>
              </div>
            </div>
          </div>

          {/* Interactive Playground */}
          <ApiPlayground endpoint={currentEndpoint} />

          {/* Code Examples */}
          <div className="bg-white border border-slate-200" data-testid="code-examples">
            <Tabs defaultValue="python" key={selectedEndpoint}>
              <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900">Code Examples</h4>
                <TabsList className="bg-slate-100 h-8">
                  <TabsTrigger value="python" className="text-xs h-7 px-3" data-testid="code-tab-python">Python</TabsTrigger>
                  <TabsTrigger value="curl" className="text-xs h-7 px-3" data-testid="code-tab-curl">cURL</TabsTrigger>
                  <TabsTrigger value="go" className="text-xs h-7 px-3" data-testid="code-tab-go">Go</TabsTrigger>
                </TabsList>
              </div>
              {Object.entries(currentEndpoint.codeExamples).map(([lang, code]) => (
                <TabsContent key={lang} value={lang} className="m-0">
                  <div className="relative bg-slate-950 p-4 overflow-auto max-h-80">
                    <button onClick={() => handleCopy(code)} className="absolute top-3 right-3 p-1.5 text-slate-500 hover:text-slate-300 transition-colors" data-testid={`copy-${lang}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <pre className="text-xs font-mono text-slate-300 leading-relaxed whitespace-pre">{code}</pre>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
        </TabsContent>

        {/* ── SDK Downloads ──────────────────────────────────────────────── */}
        <TabsContent value="sdks" className="mt-0">
          <SDKDownloads onCopy={handleCopy} copied={copied} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
